import React from "react";
import { CheckCircle2, AlertTriangle, HelpCircle, Circle, ShieldAlert, AlertOctagon } from "lucide-react";
import { cn } from "@/lib/utils";

// v0.2 accessible status model — never color-alone; each state keeps text + a
// distinct icon. REVIEW_REQUIRED = red (human attention, NOT "denied").
// UNABLE_TO_EVALUATE = indigo (no applicable policy). RED = reviewer high-risk flag.
const MAP = {
  READY: { label: "READY", cls: "bg-emerald-50 text-emerald-800 border-emerald-400", Icon: CheckCircle2 },
  ACTION_NEEDED: { label: "ACTION NEEDED", cls: "bg-amber-50 text-amber-900 border-amber-400", Icon: AlertTriangle },
  REVIEW_REQUIRED: { label: "REVIEW REQUIRED", cls: "bg-rose-50 text-rose-800 border-rose-400", Icon: AlertOctagon },
  UNABLE_TO_EVALUATE: { label: "UNABLE TO EVALUATE", cls: "bg-indigo-50 text-indigo-800 border-indigo-400", Icon: HelpCircle },
  RED: { label: "HIGH RISK — REVIEW", cls: "bg-rose-100 text-rose-900 border-rose-500", Icon: ShieldAlert },
  SATISFIED: { label: "SATISFIED", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  MISSING: { label: "MISSING", cls: "bg-amber-50 text-amber-900 border-amber-400", Icon: AlertTriangle },
  UNABLE: { label: "UNABLE TO DETERMINE", cls: "bg-indigo-50 text-indigo-800 border-indigo-300", Icon: HelpCircle },
};

const fallback = (s) => ({
  label: (s || "—").replace(/_/g, " "),
  cls: "bg-slate-100 text-slate-600 border-slate-300",
  Icon: Circle,
});

export function StatusBadge({ status, className, size = "md", plain = false }) {
  const m = MAP[status] || fallback(status);
  const { Icon } = m;
  const label = plain ? m.label.split(" · ").pop() : m.label;
  return (
    <span
      data-testid={`status-badge-${(status || "unknown").toLowerCase()}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border font-semibold uppercase tracking-wide",
        size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs",
        m.cls, className,
      )}
    >
      <Icon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
      {label}
    </span>
  );
}

const DOT = {
  GREEN: "bg-emerald-500", YELLOW: "bg-amber-500", RED: "bg-rose-500",
  REVIEW: "bg-rose-500", UNABLE: "bg-indigo-500", BLUE: "bg-sky-500",
};

export function ColorDot({ color, label, className }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium text-slate-600", className)}>
      <span className={cn("h-2.5 w-2.5 rounded-full", DOT[color] || "bg-slate-300")} />
      {label || color}
    </span>
  );
}

export default StatusBadge;
